import Router from 'next/router';
import DefaultLayout from 'src/layouts/default';
import CommunityLayout from 'src/layouts/community';
import ErrorPage from 'src/components/error';
import * as log from 'loglevel';
import { PageContext } from 'src/types';
import { Community } from 'src/types/community';

export type Page = any;

export class RequestError extends Error {
  response: {
    status: number;
  };
  constructor(message: string, status: number, ...params: any) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(...params);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RequestError);
    }

    this.name = String(status);
    this.message = message;
    this.response = { status };
  }
}

interface PageContextOrError extends PageContext {
  error: boolean;
  status: number;
  currentCommunity: Community,
}

const withErrorHandling = (Child: any) => {
  const WrappedComponent = (props: PageContextOrError) => {
    if (props.error) {
      if (props.currentCommunity) {
        return (
          <CommunityLayout containerClassName="full-width">
            <ErrorPage status={props.status}/>
          </CommunityLayout>
        );
      } else {
        return <DefaultLayout><ErrorPage status={props.status}/></DefaultLayout>;
      }
    }
    return <Child {...props} />;
  };

  WrappedComponent.getInitialProps = async (props: PageContext) => {
    try {
      return await Child.getInitialProps(props);
    } catch (e) {
      log.error(e, ', Failed fetching initial props for page: ' + Child.displayName);
      const status = e.response?.status || 500;
      const currentCommunity = e.response?.currentCommunity;
      const isLoginRequired = (status: number) => !props.currentUser && (status === 310 || status === 401);

      if (isLoginRequired(status)) {
        if (props.res) {
          props.res.writeHead(302, { Location: `/login${props.asPath}` }).end();
        } else {
          Router.push(`/login${props.asPath}`);
        }
      } else {
        return {
          error: true,
          currentCommunity,
          status,
          namespacesRequired: ['common'],
        };
      }
    }
  };

  return WrappedComponent;
};

export default withErrorHandling;
